import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { UserRole } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Get the label for a user role
 */
export const getRoleLabel = (role: UserRole): string => {
  switch (role) {
    case UserRole.USER:
      return "Utente";
    case UserRole.ADMIN:
      return "Operatore";
    case UserRole.MASTER:
      return "Master";
    case UserRole.SERVICE:
      return "Servizio";
    case UserRole.MANAGER:
      return "Manager";
    default:
      return role;
  }
};

/**
 * Custom hook to update user roles.
 * @returns Object containing the updateRole function and loading state
 */
function useUpdateRole() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { successToast, errorToast } = useShowToast();

  const updateRole = async (selectedUserIds: number[], role: UserRole) => {
    setIsLoading(true);
    try {
      await api.bulkUpdateUsers({
        user_ids: selectedUserIds,
        role,
      });
      if (api?.error) {
        errorToast({
          title: t("club_management.users.update_role.error_title"),
          description: api.error?.data as string,
        });
        return;
      }
      successToast({
        title: t("club_management.users.update_role.success_title"),
        description: t("club_management.users.update_role.success_description", {
          count: selectedUserIds.length,
          role: getRoleLabel(role),
        }),
      });
    } catch (error) {
      errorToast({
        title: t("club_management.users.update_role.error_title"),
        description: t("club_management.users.update_role.error_description"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateRole,
    isLoading,
  };
}

export default useUpdateRole;
